import { ProcessImportCheckBody } from "@via/api/process-git-import.api.tsx";
import { useIntegrationTokens } from "@via/api/integration-tokens.api.tsx";
import { JSX, useMemo } from "react";

type GitProvider = ProcessImportCheckBody["provider"];

/**
 * Step 1: point at a repository and, if it is private, the credential that can read it.
 *
 * The credential picker is copied from the connect panel in
 * `create-edit-process-form/script-assets.tsx:448-480`, filtering by the chosen provider so a
 * github credential never shows up under gitlab. Nothing here is preselected: `serviceTokenId`
 * starting `null` normalizes to "no credential," which is exactly right for a public repository
 * check, and a user who does need one has to choose it.
 */
export default function SourceStep({
  repoUrl,
  onRepoUrlChange,
  provider,
  onProviderChange,
  serviceTokenId,
  onServiceTokenIdChange,
  onCheck,
  isChecking,
  errorMessage,
}: {
  repoUrl: string;
  onRepoUrlChange: (value: string) => void;
  provider: GitProvider;
  onProviderChange: (value: GitProvider) => void;
  serviceTokenId: string | null;
  onServiceTokenIdChange: (value: string | null) => void;
  onCheck: () => void;
  isChecking: boolean;
  errorMessage: string | null;
}): JSX.Element {
  // 100 rather than the default 10, matching the connect panel this picker is copied from: a
  // picker that silently omits a credential past the caller's page cap is worse than a page
  // nobody scrolls to the end of.
  const { data: credentialPage } = useIntegrationTokens(0, 100);

  const matchingCredentials = useMemo(
    () =>
      (credentialPage?.data ?? [])
        .filter(credential => credential.tokenType === provider)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
    [credentialPage, provider]
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Point at the repository that publishes the process. The connection is
        checked before anything is stored.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <input
          className="rounded border border-gray-300 px-2 py-1 text-sm sm:col-span-2"
          placeholder="https://github.com/owner/repo.git"
          value={repoUrl}
          aria-label="Repository URL"
          onChange={event => onRepoUrlChange(event.target.value)}
        />
        <select
          className="rounded border border-gray-300 px-2 py-1 text-sm"
          value={provider}
          aria-label="Provider"
          onChange={event =>
            onProviderChange(event.target.value as GitProvider)
          }>
          <option value="github">github</option>
          <option value="gitlab">gitlab</option>
          <option value="bitbucket">bitbucket</option>
        </select>
        <div>
          <select
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
            value={serviceTokenId ?? ""}
            aria-label="Credential"
            disabled={0 === matchingCredentials.length}
            onChange={event => onServiceTokenIdChange(event.target.value)}>
            <option value="">No credential (public repository)</option>
            {matchingCredentials.map(credential => (
              <option key={credential.id} value={credential.id}>
                {credential.name}
              </option>
            ))}
          </select>
          {0 === matchingCredentials.length ? (
            <p className="mt-1 text-xs text-gray-500">
              Add a {provider} credential on the integrations page to reach a
              private repository.
            </p>
          ) : null}
        </div>
      </div>
      {null !== errorMessage ? (
        <p className="text-sm text-red-600" data-testid="importSourceError">
          {errorMessage}
        </p>
      ) : null}
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="importCheckRepository"
          disabled={"" === repoUrl.trim() || isChecking}
          onClick={onCheck}
          className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed">
          {isChecking ? "Checking..." : "Check repository"}
        </button>
      </div>
    </div>
  );
}
